import { useEffect, useRef } from 'react';

interface WaiterModalHistoryState {
  waiterModal: string;
}

function isWaiterModalState(state: unknown, key: string): boolean {
  return (
    !!state && typeof state === 'object' && (state as WaiterModalHistoryState).waiterModal === key
  );
}

type ModalHistoryPhase = 'idle' | 'pushed' | 'cleanup-pending';

/**
 * 04-ux-mobile-spec.md §4 — "every modal/sheet pushes a history entry on
 * open (`history.pushState({modal:'cart'})`) and closes on `popstate`" so
 * the Android hardware/gesture back button dismisses the top-most waiter
 * modal instead of leaving the ticket screen underneath it.
 *
 * `key` disambiguates stacked modals (cart open, then options on top of it):
 * each instance only reacts to a `popstate` that popped past ITS OWN entry
 * (`event.state` no longer carries this `key`) — one physical back press
 * pops exactly one entry, so only the top-most modal closes, not both.
 *
 * Closing any other way (Annuler / Valider / overlay tap / Escape) consumes
 * the pushed entry in the unmount cleanup so a later back press does not
 * need an extra "phantom" press once the modal is already gone.
 *
 * React 18 StrictMode (dev only) synchronously mounts, cleans up, and
 * re-mounts every effect once. The original version pushed on every mount
 * and popped (via the async `history.back()`) on every cleanup, so the
 * phantom StrictMode cleanup queued a `back()` that only resolved *after*
 * the real remount had pushed its own fresh entry — the real modal's own
 * `popstate` handler then received that late `back()` as if the user had
 * pressed Back, closing the modal the instant it opened (reproduced with a
 * scripted pushState/back trace: MOUNT#2 immediately receives a popstate
 * with the pre-modal state and calls `onClose`).
 *
 * `phaseRef`/`pushedKeyRef` survive across the StrictMode double-invoke
 * (same ref instance, only the effect body re-runs) so the real remount —
 * which happens synchronously, in the same tick, before any microtask can
 * run — can see its own phantom cleanup still pending and cancel it instead
 * of racing it. The actual `history.back()` for a genuine close is deferred
 * to a microtask for the same reason: only a real unmount reaches it.
 */
export function useModalBackClose(key: string, onClose: () => void): void {
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  const phaseRef = useRef<ModalHistoryPhase>('idle');
  const pushedKeyRef = useRef<string | null>(null);

  useEffect(() => {
    if (phaseRef.current === 'cleanup-pending' && pushedKeyRef.current === key) {
      // Immediately-preceding cleanup was a StrictMode phantom for this same
      // key — cancel its deferred pop below, we're still open.
      phaseRef.current = 'pushed';
    } else {
      window.history.pushState({ waiterModal: key }, '');
      phaseRef.current = 'pushed';
      pushedKeyRef.current = key;
    }

    const handlePopState = (event: PopStateEvent) => {
      if (isWaiterModalState(event.state, key)) return; // a deeper modal's entry — not mine
      phaseRef.current = 'idle';
      onCloseRef.current();
    };
    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
      if (phaseRef.current !== 'pushed') return; // already closed via popstate, or never pushed
      phaseRef.current = 'cleanup-pending';
      queueMicrotask(() => {
        if (phaseRef.current !== 'cleanup-pending') return; // cancelled by a synchronous remount
        phaseRef.current = 'idle';
        if (isWaiterModalState(window.history.state, key)) {
          window.history.back();
        }
      });
    };
  }, [key]);
}
